/**
 * Ticket WK1: Grundlage des Wahlkalenders — Gemeinden einlesen, Amtszeiten
 * ansehen. Ticket WK23: Der Wikidata-Abgleich ist entfernt.
 *
 * Kein Dashboard, keine Kacheln, keine zeitgesteuerten Auslöser: die Seite
 * trägt ausschließlich die Datengrundlage.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Calculator, Check, FileText, RefreshCcw, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  acceptMunicipalityReviewProposal,
  dismissMunicipalityReviewProposal,
  dismissMunicipalityReviewTile,
  importMunicipalities,
  listMunicipalities,
  listMunicipalityDistricts,
  listMunicipalityReviews,
  listTermLengths,
  runMunicipalityPressScan,
  runMunicipalityReviewTriggers,
} from "@/lib/wahlkalender.functions";
import {
  BUNDESLAND_LABELS,
  ELECTION_PERIODS,
  ELECTION_PERIOD_LABELS,
  ELECTION_SOURCE_LABELS,
  MAYOR_OFFICE_LABELS,
  MUNICIPALITY_REQUIRED_FIELDS,
  PROPOSAL_FIELD_LABELS,
  proposalExplanation,
  REVIEW_REASON_LABELS,
  REVIEW_SOURCE_LABELS,
  RUNS_AGAIN_LABELS,
  RUNS_AGAIN_VALUES,
  parseMunicipalitySheet,
  type Bundesland,
  type ElectionPeriod,
  type ParseResult,
  type RunsAgain,
} from "@/lib/wahlkalender";
import { BUNDESLAENDER } from "@/lib/contacts";
import { MUNICIPALITY_ACCEPT, readMunicipalityFile } from "@/lib/wahlkalender-file";
import { describePressRun } from "@/lib/wahlkalender-presse";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminTableShell } from "@/components/admin/AdminTableShell";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminSkeletonList } from "@/components/admin/AdminSkeletonList";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";
import { AdminField, adminInputClass } from "@/components/admin/AdminField";
import { MunicipalityEditForm } from "@/components/admin/WahlkalenderMunicipalityEdit";
import { InlineEditCell } from "@/components/admin/WahlkalenderInlineEdit";
import { LandesseitePublishCell } from "@/components/admin/WahlkalenderLandesseiten";
import { useAdminSession } from "@/hooks/use-admin-session";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MunicipalityRow, ReviewTileRow } from "@/lib/wahlkalender.server";

// Ticket WK12: Die Gemeindeliste blättert in Seiten von 250 Einträgen.
const MUNICIPALITY_PAGE_SIZE = 250;

export const Route = createFileRoute("/_admin/admin/wahlkalender")({
  // Diese umfangreiche interne Seite bleibt bewusst in ihrem Routenmodul.
  // Der getrennte Komponentenabruf brach in der Vorschau wiederholt ab und
  // machte ausschließlich /admin/wahlkalender unerreichbar.
  codeSplitGroupings: [],
  component: WahlkalenderPage,
  head: () => ({ meta: [{ title: "Wahlkalender – Interner Bereich" }] }),
});

function formatDay(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/** Kleines unauffälliges Zeichen in Textfarbe mit Hinweis beim Darüberfahren. */
function ElectionDateMark({
  source,
  dayKnown,
}: {
  source: MunicipalityRow["electionSource"];
  dayKnown: boolean;
}) {
  if (!source) return null;
  const Icon = source === "meldung" ? FileText : Calculator;
  const hint = `${ELECTION_SOURCE_LABELS[source]}${dayKnown ? "" : " – Tag nicht bekannt, dargestellt ist der Monatserste"}`;
  return (
    <span className="inline-flex text-foreground/70" title={hint} aria-label={hint} role="img">
      <Icon className="h-3.5 w-3.5" aria-hidden />
    </span>
  );
}

/** Eine Auswahl über Werten mit „alle"-Leerstelle. */
function FilterSelect({
  label,
  value,
  onChange,
  entries,
  allLabel,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  entries: Array<{ value: string; label: string }>;
  allLabel: string;
}) {
  return (
    <div>
      <span className="mb-1 block text-xs font-semibold text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={adminInputClass}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="alle">{allLabel}</SelectItem>
          {entries.map((entry) => (
            <SelectItem key={entry.value} value={entry.value}>
              {entry.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Ticket WK8: eine Kachel je Gemeinde, unabhängig von der Herkunft. Die Quelle
 * steht als eigene Markierung, die Gründe daneben. Presse-Funde tragen Titel,
 * Quelle, Datum und den Verweis zum Öffnen.
 */
function ReviewTile({
  tile,
  onOpen,
  onAccepted,
}: {
  tile: ReviewTileRow;
  onOpen: () => void;
  onAccepted: () => void;
}) {
  const articles = tile.articles ?? [];
  const acceptFn = useServerFn(acceptMunicipalityReviewProposal);
  const dismissFn = useServerFn(dismissMunicipalityReviewProposal);
  const dismissTileFn = useServerFn(dismissMunicipalityReviewTile);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const accept = async (entry: ReviewTileRow["proposals"][number]) => {
    if (entry.proposed === null) return;
    const key = `${entry.reviewId}-${entry.entryIndex}-accept`;
    setBusy(key);
    try {
      await acceptFn({
        data: {
          municipalityId: tile.municipalityId,
          reviewId: entry.reviewId,
          entryIndex: entry.entryIndex,
          field: entry.field,
          proposed: entry.proposed,
        },
      });
      toast.success(`${PROPOSAL_FIELD_LABELS[entry.field]} übernommen.`);
      onAccepted();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(null);
    }
  };

  /** Verwerfen hält den eigenen Stand fest: nichts wird geschrieben. */
  const dismiss = async (entry: ReviewTileRow["proposals"][number]) => {
    const key = `${entry.reviewId}-${entry.entryIndex}-dismiss`;
    setBusy(key);
    try {
      await dismissFn({
        data: {
          municipalityId: tile.municipalityId,
          reviewId: entry.reviewId,
          entryIndex: entry.entryIndex,
          field: entry.field,
        },
      });
      toast.success(`${PROPOSAL_FIELD_LABELS[entry.field]}: Vorschlag verworfen, unser Stand bleibt.`);
      onAccepted();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const dismissTile = async () => {
    setBusy("tile-dismiss");
    try {
      await dismissTileFn({
        data: { municipalityId: tile.municipalityId, reviewIds: tile.reviewIds },
      });
      toast.success(`${tile.name}: Vorschlag verworfen, unser Stand bleibt.`);
      setConfirmOpen(false);
      onAccepted();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-background transition-colors hover:border-brand-petrol/40">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-display text-base font-extrabold text-brand-petrol">{tile.name}</p>
            <p className="text-xs text-muted-foreground">{tile.district}</p>
          </div>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-mr-2 -mt-2 h-8 w-8 shrink-0"
                aria-label={`Vorschlag für ${tile.name} verwerfen`}
                data-testid="review-tile-dismiss"
                disabled={busy !== null}
              >
                <X aria-hidden />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Vorschlag verwerfen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Unser Stand für {tile.name} bleibt. Die Prüfkarte wird als erledigt gespeichert und
                  erscheint nicht erneut.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(event) => {
                    event.preventDefault();
                    void dismissTile();
                  }}
                  disabled={busy !== null}
                >
                  {busy === "tile-dismiss" ? "Wird verworfen …" : "Verwerfen"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <ul className="mt-2 flex flex-wrap gap-1">
          {tile.sources.map((source) => (
            <li
              key={`quelle-${source}`}
              className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold"
              data-testid="wk8-quelle"
            >
              {REVIEW_SOURCE_LABELS[source]}
            </li>
          ))}
          {tile.reasons.map((reason) => (
            <li key={reason} className="rounded-full border border-border px-2 py-0.5 text-xs">
              {REVIEW_REASON_LABELS[reason]}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-sm">
          Wahl: {formatDay(tile.electionDate)}{" "}
          <ElectionDateMark source={tile.electionSource} dayKnown={tile.electionDayKnown} />
        </p>
        <p className="text-xs text-muted-foreground">
          {tile.holderName ? `Amtiert: ${tile.holderName}` : "Keine amtierende Person eingetragen"}
        </p>
        <ul className="mt-3 space-y-1.5 text-sm">
          {tile.instructions.map((instruction) => (
            <li key={instruction}>{instruction}</li>
          ))}
        </ul>
        {tile.proposals.length > 0 ? (
          <ul className="mt-3 space-y-3">
            {tile.proposals.map((entry) => {
              const key = `${entry.reviewId}-${entry.entryIndex}`;
              return (
                <li key={key} className="rounded-md bg-muted/60 p-3 text-sm">
                  <p className="text-sm">{proposalExplanation(entry)}</p>
                  {entry.proposed !== null ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void accept(entry)}
                      >
                        <Check aria-hidden />
                        {busy === `${key}-accept` ? "Wird übernommen …" : "Übernehmen"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy !== null}
                        onClick={() => void dismiss(entry)}
                      >
                        {busy === `${key}-dismiss` ? "Wird verworfen …" : "Verwerfen"}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            Für diesen Prüfauftrag liegt kein automatisch übernehmbarer Wert vor.
          </p>
        )}
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onOpen}>
          Prüfen und bearbeiten
        </Button>
      </div>
      {articles.length > 0 ? (
        <ul className="space-y-2 border-t border-border px-4 py-3">
          {articles.map((article) => (
            <li key={article.id} className="text-xs">
              <a
                href={article.link}
                target="_blank"
                rel="noreferrer noopener"
                className="font-display font-extrabold text-brand-petrol underline"
              >
                {article.title}
              </a>
              <span className="block text-muted-foreground">
                {[
                  article.sourceName,
                  formatDay(article.publishedAt ? article.publishedAt.slice(0, 10) : null),
                ]
                  .filter((value) => value && value !== "—")
                  .join(" · ")}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function WahlkalenderPage() {

  const { isAdmin } = useAdminSession();
  const queryClient = useQueryClient();
  // Ticket WK2, Schritt 7: Filter in der Gemeindeliste. „Alle Bundesländer"
  // öffnet die Liste.
  const [bundesland, setBundesland] = useState<Bundesland | "alle">("alle");
  const [district, setDistrict] = useState<string | null>(null);
  const [runsAgain, setRunsAgain] = useState<RunsAgain | "alle">("alle");
  const [period, setPeriod] = useState<ElectionPeriod>("alle");
  const [search, setSearch] = useState("");
  // Ticket WK12: Die Liste blättert seitenweise durch alle Gemeinden.
  const [page, setPage] = useState(1);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [editing, setEditing] = useState<MunicipalityRow | null>(null);
  const [reviewOpen, setReviewOpen] = useState<ReviewTileRow | null>(null);

  const listFn = useServerFn(listMunicipalities);
  const termsFn = useServerFn(listTermLengths);
  const districtsFn = useServerFn(listMunicipalityDistricts);
  const importFn = useServerFn(importMunicipalities);
  const reviewsFn = useServerFn(listMunicipalityReviews);
  const triggersFn = useServerFn(runMunicipalityReviewTriggers);
  const pressFn = useServerFn(runMunicipalityPressScan);

  const reviews = useQuery({
    queryKey: ["admin", "municipality-reviews"],
    queryFn: () => reviewsFn(),
  });
  const tiles = reviews.data ?? [];


  const terms = useQuery({ queryKey: ["admin", "term-lengths"], queryFn: () => termsFn() });
  const districts = useQuery({
    queryKey: ["admin", "municipality-districts", bundesland],
    queryFn: () =>
      districtsFn({ data: { bundesland: bundesland === "alle" ? null : bundesland } }),
  });
  const municipalities = useQuery({
    queryKey: [
      "admin",
      "municipalities",
      bundesland,
      district,
      runsAgain,
      period,
      search,
      page,
    ],
    queryFn: () =>
      listFn({
        data: {
          bundesland: bundesland === "alle" ? null : bundesland,
          district,
          runsAgain: runsAgain === "alle" ? null : runsAgain,
          period,
          search,
          limit: MUNICIPALITY_PAGE_SIZE,
          offset: (page - 1) * MUNICIPALITY_PAGE_SIZE,
        },
      }),
  });


  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "municipalities"] });
  };

  // Schreibweg in der Zeile: derselbe Datensatz, danach nur die Liste neu.
  const refreshList = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "municipalities"] });
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!parsed) throw new Error("Bitte zuerst eine Datei wählen.");
      return importFn({
        data: {
          rowsRead: parsed.rows.length + parsed.skipped.length + parsed.duplicates.length,
          rows: parsed.rows,
          skipped: parsed.skipped,
          duplicates: parsed.duplicates,
        },
      });
    },
    onSuccess: (report) => {
      toast.success(`${report.created} neu, ${report.updated} aktualisiert.`);
      refresh();
    },
    onError: (error: unknown) => toast.error((error as Error).message),
  });


  // Ticket WK8: der Rechnungs-Lauf bleibt von Hand anstoßbar, ohne Zeitplan.
  const triggersMutation = useMutation({
    mutationFn: async () => triggersFn(),
    onSuccess: (report) => {
      toast.success(`${report.created} Fälle neu, ${report.checked} Gemeinden geprüft.`);
      void queryClient.invalidateQueries({ queryKey: ["admin", "municipality-reviews"] });
    },
    onError: (error: unknown) => toast.error((error as Error).message),
  });

  const pressMutation = useMutation({
    mutationFn: async () => pressFn(),
    onSuccess: (report) => {
      // Ticket PR2: Sperre und Fehlschläge des Kanals werden ausdrücklich
      // gemeldet, statt wie ein Lauf ohne Funde auszusehen.
      const message = describePressRun(report);
      if (message.tone === "warning") toast.warning(message.text);
      else toast.success(message.text);
      void queryClient.invalidateQueries({ queryKey: ["admin", "municipality-reviews"] });
    },
    onError: (error: unknown) => toast.error((error as Error).message),
  });

  /**
   * Ticket WK8: eine Zeile je Quelle. Eine weitere Quelle bekommt hier einen
   * weiteren Eintrag – die Leiste und die Seite bleiben unverändert.
   */
  const sourceActions = [
    {
      key: "rechnung",
      label: "Mögliche Änderungen prüfen",
      // Ticket WK11: was der Knopf wirklich tut, in einem Satz je Quelle.
      hint: "Rechnet aus Amtsantritt und Amtszeit die nächsten Wahltermine nach und legt Fälle vor, wo ein Termin überfällig oder in Sicht ist.",
      run: () => triggersMutation.mutate(),
      pending: triggersMutation.isPending,
    },
    {
      key: "presse",
      label: "Presse abrufen",
      hint: "Durchsucht den Nachrichtenkanal nach Meldungen der letzten sechs Monate – nur für Gemeinden ohne bestätigten Wahltermin.",
      run: () => pressMutation.mutate(),
      pending: pressMutation.isPending,
    },
  ];



  const onFiles = async (files: FileList) => {
    const file = files[0];
    if (!file) return;
    setReading(true);
    try {
      const matrix = await readMunicipalityFile(file);
      const result = parseMunicipalitySheet(matrix);
      setParsed(result);
      setFileName(file.name);
      if (result.missingColumns.length > 0) {
        toast.error("Die Datei trägt keine lesbare Kopfzeile mit Gemeinde, Bundesland und Landkreis.");
      }

    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setReading(false);
    }
  };

  const importReport = importMutation.data ?? null;

  const rows = municipalities.data?.rows ?? [];
  const termRows = terms.data ?? [];

  // Ticket WK1a: welche Bundesländer stecken in der gewählten Datei?
  const parsedBundeslaender = useMemo(() => {
    if (!parsed) return [];
    const counts = new Map<Bundesland, number>();
    for (const row of parsed.rows) counts.set(row.bundesland, (counts.get(row.bundesland) ?? 0) + 1);
    return [...counts.entries()]
      .map(([bundesland, count]) => ({ bundesland, count }))
      .sort((a, b) => b.count - a.count);
  }, [parsed]);

  return (

    <div className="space-y-8">
      <AdminPageHeader
        title="Wahlkalender"
        description="Gemeinden, amtierende Personen und Amtszeiten. Das Ende der Amtszeit wird nicht gespeichert, sondern jeweils aus Amtsantritt und Amtszeit berechnet."
        actions={
          // Ticket WK8: eine Leiste je Quelle. Eine weitere Quelle bekommt hier
          // einen weiteren Eintrag – die Seite muss dafür nicht umgebaut werden.
          <div className="flex flex-wrap gap-2">
            {sourceActions.map((action) => (
              <Button
                key={action.key}
                variant="outline"
                onClick={action.run}
                disabled={action.pending}
              >
                <RefreshCcw className="mr-2 h-4 w-4" aria-hidden />
                {action.pending ? "Läuft …" : action.label}
              </Button>
            ))}
          </div>
        }

      />

      {/* Ticket WK11: ein Erklärsatz je Quelle, damit klar ist, was der Knopf auslöst. */}
      <ul className="-mt-4 space-y-1 text-sm text-muted-foreground">
        {sourceActions.map((action) => (
          <li key={`hint-${action.key}`}>
            <span className="font-medium text-foreground">{action.label}:</span> {action.hint}
          </li>
        ))}
      </ul>




      {/* Ticket WK8: alle Fälle an einer Stelle, je Eintrag mit erkennbarer Quelle. */}
      <AdminCard
        title="Mögliche Änderungen"
        description={`${tiles.length} Gemeinde${tiles.length === 1 ? "" : "n"} zur Prüfung – aus Rechnung, Presse und Kundenmeldungen. Eine Gemeinde ergibt eine Kachel, mehrere Gründe stehen nebeneinander. Derselbe Auslöser erzeugt keinen neuen Fall.`}
      >
        {reviews.isLoading ? (
          <AdminSkeletonList rows={3} />
        ) : reviews.error ? (
          <AdminErrorNotice message={(reviews.error as Error).message} />
        ) : tiles.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine offenen Fälle.</p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {tiles.map((tile) => (
              <li key={tile.municipalityId}>
                <ReviewTile
                  tile={tile}
                  onOpen={() => setReviewOpen(tile)}
                  onAccepted={() => {
                    void queryClient.invalidateQueries({ queryKey: ["admin", "municipality-reviews"] });
                    void queryClient.invalidateQueries({ queryKey: ["admin", "municipalities"] });
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </AdminCard>

      <AdminCard
        title="Amtszeiten"
        description="Dauer je Bundesland und Amtsart. Die Freischaltung gilt für die öffentliche Seite /wahlkalender/… – ohne Freischaltung ist sie für Besucher nicht erreichbar."
      >
        {terms.isLoading ? (
          <AdminSkeletonList rows={2} />
        ) : terms.error ? (
          <AdminErrorNotice message={(terms.error as Error).message} />
        ) : termRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Amtszeit gepflegt.</p>
        ) : (
          <AdminTableShell maxHeightClass="max-h-[40vh]">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="text-left">
                  <th className="p-3">Bundesland</th>
                  <th className="p-3">Amtsart</th>
                  <th className="p-3">Dauer</th>
                  <th className="p-3">Lauf</th>
                  <th className="p-3">Öffentliche Seite</th>
                </tr>
              </thead>
              <tbody>
                {termRows.map((term) => (
                  <tr key={`${term.bundesland}-${term.office}-${term.validFrom}`} className="border-t border-border">
                    <td className="p-3">{BUNDESLAND_LABELS[term.bundesland]}</td>
                    <td className="p-3">{MAYOR_OFFICE_LABELS[term.office]}</td>
                    <td className="p-3">{term.years} Jahre</td>
                    <td className="p-3">
                      {term.mode === "individuell" ? "ab eigenem Amtsantritt" : "gemeinsamer Wahltermin"}
                    </td>
                    <td className="p-3">
                      <LandesseitePublishCell bundesland={term.bundesland} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableShell>
        )}

        {isAdmin ? (
          <div className="mt-6 space-y-4 border-t border-border pt-6">
            <p className="text-sm text-muted-foreground">
              Excel oder CSV. Erste Zeile darf Gruppenüberschriften tragen, die Spaltenzeile muss Gemeinde, Bundesland
              und Landkreis nennen. Fehlerhafte Zeilen stoppen den Vorgang nicht; das Bundesland steht je Zeile in der
              Datei.
            </p>
            <AdminUploadButton
              label="Datei wählen"
              accept={MUNICIPALITY_ACCEPT}
              uploading={reading}
              currentName={fileName}
              hint={`Erwartete Spalten: ${MUNICIPALITY_REQUIRED_FIELDS.length} Pflichtangaben (Gemeinde, Bundesland, Landkreis, Amt, Monat, Jahr) sowie Einwohner, Name, Vorname, PLZ und optional Tag.`}
              onFiles={(files) => void onFiles(files)}
            />

            {parsed ? (
              <div className="space-y-3 rounded-md border border-border p-4 text-sm">
                <p>
                  <strong>{parsed.rows.length}</strong> lesbare Zeilen,{" "}
                  <strong>{parsed.skipped.length}</strong> übersprungen,{" "}
                  <strong>{parsed.duplicates.length}</strong> mehrfach genannt.
                </p>
                {parsedBundeslaender.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Bundesländer in der Datei:{" "}
                    {parsedBundeslaender
                      .map((entry) => `${BUNDESLAND_LABELS[entry.bundesland]} (${entry.count})`)
                      .join(", ")}
                  </p>
                ) : null}

                {parsed.skipped.length > 0 ? (
                  <details>
                    <summary className="cursor-pointer font-semibold">Übersprungene Zeilen</summary>
                    <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                      {parsed.skipped.slice(0, 200).map((row) => (
                        <li key={`s-${row.rowNumber}`}>
                          Zeile {row.rowNumber} – {row.name}: {row.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
                {parsed.duplicates.length > 0 ? (
                  <details>
                    <summary className="cursor-pointer font-semibold">Mehrfach genannte Gemeinden</summary>
                    <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                      {parsed.duplicates.slice(0, 200).map((row) => (
                        <li key={`d-${row.rowNumber}`}>
                          Zeile {row.rowNumber} – {row.name}: {row.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
                <Button
                  onClick={() => importMutation.mutate()}
                  disabled={parsed.rows.length === 0 || importMutation.isPending}
                >
                  {importMutation.isPending ? "Wird übernommen …" : "Übernehmen"}
                </Button>
              </div>
            ) : null}

            {importReport ? (
              <p className="text-sm">
                Übernommen: <strong>{importReport.created}</strong> neu,{" "}
                <strong>{importReport.updated}</strong> aktualisiert, {importReport.skipped.length} übersprungen.
              </p>
            ) : null}
          </div>
        ) : null}
      </AdminCard>


      <AdminCard
        title="Gemeinden"
        description={`${municipalities.data?.total ?? 0} Gemeinden in dieser Auswahl. Das Ende der Amtszeit und das Wahldatum sind berechnet, sofern kein Termin bestätigt ist.`}
      >
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FilterSelect
              label="Bundesland"
              allLabel="Alle Bundesländer"
              value={bundesland}
              onChange={(next) => {
                setBundesland(next as Bundesland | "alle");
                setDistrict(null);
                setPage(1);
              }}
              entries={BUNDESLAENDER.map((value) => ({
                value,
                label: BUNDESLAND_LABELS[value],
              }))}
            />
            <FilterSelect
              label="Landkreis"
              allLabel="Alle Landkreise"
              value={district ?? "alle"}
              onChange={(next) => {
                setDistrict(next === "alle" ? null : next);
                setPage(1);
              }}
              entries={(districts.data ?? []).map((value) => ({ value, label: value }))}
            />
            <FilterSelect
              label="Zeitraum bis zum Wahldatum"
              allLabel={ELECTION_PERIOD_LABELS.alle}
              value={period}
              onChange={(next) => {
                setPeriod(next as ElectionPeriod);
                setPage(1);
              }}
              entries={ELECTION_PERIODS.filter((value) => value !== "alle").map((value) => ({
                value,
                label: ELECTION_PERIOD_LABELS[value],
              }))}
            />
            <FilterSelect
              label="Tritt erneut an"
              allLabel="Alle Angaben"
              value={runsAgain}
              onChange={(next) => {
                setRunsAgain(next as RunsAgain | "alle");
                setPage(1);
              }}
              entries={RUNS_AGAIN_VALUES.map((value) => ({
                value,
                label: RUNS_AGAIN_LABELS[value],
              }))}
            />
          </div>

          <AdminField label="Suche">
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Gemeinde oder Name der amtierenden Person"
              />
            )}
          </AdminField>


          {municipalities.isLoading ? (
            <AdminSkeletonList rows={5} />
          ) : municipalities.error ? (
            <AdminErrorNotice message={(municipalities.error as Error).message} />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Gemeinde gefunden.</p>
          ) : (
            <AdminTableShell>
              <table className="w-full min-w-[1100px] text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="p-3">Gemeinde</th>
                    <th className="p-3">Bundesland</th>
                    <th className="p-3">Landkreis</th>
                    <th className="p-3">Einwohner</th>
                    <th className="p-3">Amt</th>
                    <th className="p-3">Amtierende Person</th>
                    <th className="p-3">Amtsantritt</th>
                    <th className="p-3">Wahldatum</th>
                    <th className="p-3">Tritt erneut an</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="p-3">
                        <button
                          type="button"
                          className="text-left font-semibold underline-offset-2 hover:underline"
                          onClick={() => setEditing(row)}
                          aria-label={`${row.name} – Detailansicht`}
                        >
                          {row.name}
                        </button>
                      </td>
                      <td className="p-3">{BUNDESLAND_LABELS[row.bundesland]}</td>
                      <td className="p-3">{row.district}</td>
                      <td className="p-3">{row.population?.toLocaleString("de-DE") ?? "—"}</td>
                      <td className="p-3">{row.officeLabel}</td>
                      <td className="p-3">
                        <InlineEditCell
                          row={row}
                          field="holder"
                          label="Name der amtierenden Person"
                          onSaved={refreshList}
                        />
                      </td>
                      <td className="p-3">
                        <InlineEditCell
                          row={row}
                          field="termStart"
                          label="Amtsantritt"
                          onSaved={refreshList}
                        />
                      </td>
                      <td className="p-3">
                        <InlineEditCell
                          row={row}
                          field="confirmed"
                          label="Bestätigter Wahltermin"
                          onSaved={refreshList}
                          displayOverride={
                            <span className="inline-flex items-center gap-1">
                              {formatDay(row.electionDate)}
                              <ElectionDateMark
                                source={row.electionSource}
                                dayKnown={row.electionDayKnown}
                              />
                            </span>
                          }
                        />
                      </td>
                      <td className="p-3">
                        <InlineEditCell
                          row={row}
                          field="runsAgain"
                          label="Tritt erneut an"
                          onSaved={refreshList}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

            </AdminTableShell>
          )}

          {/* Ticket WK12: Blätterleiste über alle Gemeinden der Auswahl. */}
          {(municipalities.data?.total ?? 0) > MUNICIPALITY_PAGE_SIZE && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {`${((page - 1) * MUNICIPALITY_PAGE_SIZE + 1).toLocaleString("de-DE")}–${Math.min(
                  page * MUNICIPALITY_PAGE_SIZE,
                  municipalities.data?.total ?? 0,
                ).toLocaleString("de-DE")} von ${(municipalities.data?.total ?? 0).toLocaleString("de-DE")}`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={page <= 1 || municipalities.isFetching}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Zurück
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    page * MUNICIPALITY_PAGE_SIZE >= (municipalities.data?.total ?? 0) ||
                    municipalities.isFetching
                  }
                  onClick={() => setPage((current) => current + 1)}
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}

        </div>
      </AdminCard>

      <Dialog
        open={reviewOpen !== null}
        onOpenChange={(open) => {
          if (!open) setReviewOpen(null);
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-brand-petrol">
              {reviewOpen ? reviewOpen.name : ""} prüfen
            </DialogTitle>
            <DialogDescription>
              Ein Vorschlag wird nur mit Haken übernommen. Jede Angabe lässt sich von Hand ändern;
              der Abschluss schreibt die Werte, auch ohne Änderung.
            </DialogDescription>
          </DialogHeader>
          {reviewOpen ? (
            <MunicipalityEditForm
              municipality={reviewOpen.municipality}
              mode="abschließen"
              proposals={reviewOpen.proposals}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ["admin", "municipality-reviews"] });
                void queryClient.invalidateQueries({ queryKey: ["admin", "municipalities"] });
                setReviewOpen(null);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>


      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-brand-petrol">
              {editing ? editing.name : ""} bearbeiten
            </DialogTitle>
            <DialogDescription>
              Das Ende der Amtszeit und alle Angaben, die sich praktisch nie ändern (Landkreis,
              Postleitzahl, Einwohner, Amt), sind hier frei änderbar. Ein bestätigter Wahltermin
              wird von keinem Einlesen überschrieben.
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <MunicipalityEditForm
              municipality={editing}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ["admin", "municipalities"] });
                setEditing(null);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

    </div>
  );
}
